import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';

import { useInsuranceVerificationMutation } from '@/api/hooks/mutations/use-insurance-verification-mutation';
import animationData from '@/assets/lotties/eligibility-loader.json';
import { Heading1 } from '@/components/common/Heading1.tsx';
import { Lottie } from '@/components/common/lottie-web/Lottie.tsx';
import StepperCard from '@/components/common/StepperCard.tsx';
import { NavigationBar } from '@/components/NavigationBar';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card.tsx';
import { useSessionStore } from '@/hooks/use-session-store/use-session-store';
import { logger } from '@/lib/logger';
import {
  type PostFamilyResolutionDestination,
  getDefaultResolveFamily,
  getPostFamilyResolutionDestination,
  getPreviousResolvedFamily,
  getResolveBackOverride,
  resolveFamily,
  setUnverifiedFamilyInsurances,
  setVerifiedFamilyInsurances,
} from '@/types/insurance-builder-eligibility-revamp.ts';
import {
  CARRIER_TYPE,
  type PlanFamily,
  type QueuedInsurance,
  carrierTypeToFamily,
  prettyTypeName,
} from '@/types/insurance.type.ts';

const CONFIRMED_DELAY_MS = 2200;

type ConfirmationDestination = PostFamilyResolutionDestination;

function getActiveVerificationInsuranceFamily(): PlanFamily {
  const { insuranceBuilder } = useSessionStore.getState();
  const queuedInsurance = insuranceBuilder.queuedInsurances[0];

  // Verifying is scoped to the submitted insurance. Fall back only if the queue is empty.
  if (queuedInsurance) {
    return carrierTypeToFamily(queuedInsurance.carrier.carrierType);
  }

  return getDefaultResolveFamily(insuranceBuilder);
}

function getInsuranceDisplayName(insurance: QueuedInsurance): string {
  const { carrier } = insurance;

  switch (carrier.carrierType) {
    case CARRIER_TYPE.MEDICARE:
    case CARRIER_TYPE.MEDICAID:
      return prettyTypeName(carrier.carrierType);
    case CARRIER_TYPE.MEDICARE_ADVANTAGE:
    case CARRIER_TYPE.MEDICAID_MANAGED_CARE:
      return carrier.displayName || carrier.name || prettyTypeName(carrier.carrierType);
  }
}

function formatInsuranceList(insurances: QueuedInsurance[]): string {
  const names = insurances.map(getInsuranceDisplayName);

  if (names.length === 0) return 'your selected plan';
  if (names.length === 1) return names[0]!;
  if (names.length === 2) return `${names[0]} and ${names[1]}`;

  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
}

function AnimatedShieldCheck({ className }: { className?: string }) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 38 37" fill="none" className={className}>
      <path
        d="M19.6395 0.602091C19.4417 0.327623 19.0208 0.327623 18.823 0.602091C14.277 6.91192 5.53533 6.78927 3.32834 6.64945C3.0523 6.63197 2.8125 6.84986 2.8125 7.12643V16.0878C2.8125 22.6123 5.63824 28.8598 10.8585 32.4286C13.8363 34.4643 16.9987 36.3761 19.1154 36.9685C19.1918 36.9899 19.2707 36.9899 19.3471 36.9685C21.4638 36.3761 24.6262 34.4643 27.604 32.4286C32.8243 28.8598 35.65 22.6123 35.65 16.0878V7.12643C35.65 6.84986 35.4102 6.63197 35.1342 6.64945C32.9272 6.78927 24.1855 6.91192 19.6395 0.602091Z"
        fill="url(#paint0_linear_shield_animated)"
        className="animate-in duration-300 fade-in"
      />
      <path
        d="M19.4124 5.21921C19.2096 4.94939 18.7903 4.94939 18.5875 5.21921C15.1822 9.75148 8.86292 9.7667 7.02578 9.66721C6.74967 9.65223 6.51245 9.87062 6.51245 10.1471V16.807C6.51245 21.7474 8.6616 26.478 12.632 29.1804C14.8838 30.7131 17.2745 32.153 18.8843 32.6103C18.9603 32.6319 19.0396 32.6319 19.1156 32.6103C20.7254 32.153 23.1161 30.7131 25.3679 29.1804C29.3383 26.478 31.4875 21.7474 31.4875 16.807V10.1471C31.4875 9.87062 31.2502 9.65223 30.9741 9.66721C29.137 9.7667 22.8178 9.75148 19.4124 5.21921Z"
        fill="#489E9A"
        className="animate-in duration-300 fade-in"
      />
      <g style={{ transformOrigin: '19px 18px' }}>
        <path
          d="M23.7961 14.3546C24.1273 14.0234 24.6642 14.0234 24.9953 14.3546C25.3265 14.6857 25.3265 15.2226 24.9953 15.5537L18.2118 22.3368C18.0528 22.4958 17.8373 22.5852 17.6125 22.5852C17.3876 22.5852 17.1721 22.4958 17.0131 22.3368L13.9296 19.2537C13.5985 18.9226 13.5985 18.3857 13.9296 18.0546C14.2608 17.7234 14.7977 17.7234 15.1288 18.0546L17.6125 20.5382L23.7961 14.3546Z"
          fill="#FFFCF2"
          className="checkmark-path"
        />
      </g>
      <defs>
        <linearGradient
          id="paint0_linear_shield_animated"
          x1="19.2313"
          y1="-9.34702e-05"
          x2="19.2313"
          y2="36.9999"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#79C2BF" />
          <stop offset="1" stopColor="#BCE5D5" />
        </linearGradient>
      </defs>
      <style>
        {`
          .checkmark-path {
            opacity: 0;
            transform: scale(0);
            transform-origin: center;
            animation: checkmark-pop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) 0.3s forwards;
          }
          @keyframes checkmark-pop {
            0% {
              opacity: 0;
              transform: scale(0);
            }
            70% {
              opacity: 1;
              transform: scale(1.2);
            }
            100% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </svg>
  );
}

export function InsuranceVerifying() {
  const navigate = useNavigate();
  const { override, preserveLaterFamilies, previousPlanFamily, previousUploadPlanType } = useSearch(
    {
      from: '/appLayout/wizardLayout/insurance/verifying',
    }
  );
  const [confirmationDestination, setConfirmationDestination] =
    useState<ConfirmationDestination | null>(null);
  const submittedFamilyRef = useRef<PlanFamily | null>(null);

  const {
    patientFirstName,
    patientLastName,
    patientDateOfBirth,
    patientGender,
    insuranceZipCode,
    insuranceBuilder,
  } = useSessionStore();
  const insuranceDisplayName = formatInsuranceList(insuranceBuilder.queuedInsurances);

  const verification = useInsuranceVerificationMutation();
  const navigateAfterConfirmation = (destination: ConfirmationDestination) => {
    if (destination.type === 'resolve') {
      navigate({
        to: '/insurance/resolve',
        search: {
          planFamily: destination.planFamily,
          previousPlanFamily: destination.previousPlanFamily,
          ...(preserveLaterFamilies && { preserveLaterFamilies }),
        },
        replace: true,
      });
      return;
    }

    if (destination.type === 'ineligible') {
      navigate({
        to: '/insurance/ineligible',
        search: { reason: destination.reason },
        replace: true,
      });
      return;
    }

    navigate({ to: '/insurance/benefits', replace: true });
  };

  const verifyAndRedirect = () => {
    const submittedFamily = getActiveVerificationInsuranceFamily();
    submittedFamilyRef.current = submittedFamily;
    const previousSubmittedFamily =
      previousPlanFamily ?? getPreviousResolvedFamily(insuranceBuilder, submittedFamily);

    if (!patientGender) {
      logger.error('Insurance verification failed: patient gender is required');
      navigate({ to: '/1', replace: true });
      return;
    }

    verification.mutate(
      {
        patient: {
          firstName: patientFirstName,
          lastName: patientLastName,
          dateOfBirth: patientDateOfBirth,
          gender: patientGender,
          ...(insuranceZipCode && { zipCode: insuranceZipCode }),
        },
        insurances: insuranceBuilder.queuedInsurances,
      },
      {
        onSuccess: ({ verified, unverified }) => {
          let updatedBuilder = setVerifiedFamilyInsurances(
            setUnverifiedFamilyInsurances(insuranceBuilder, ...unverified),
            ...verified
          );

          // Mark the family as resolved if verification succeeded
          for (const ins of verified) {
            const family = carrierTypeToFamily(ins.carrier.carrierType);
            updatedBuilder = resolveFamily(updatedBuilder, family, true);
          }

          // If there are unverified insurances, handle retry logic
          if (unverified.length > 0) {
            const firstUnverified = unverified[0]!;
            const failedFamily = carrierTypeToFamily(firstUnverified.carrier.carrierType);

            if (!firstUnverified.verifiable || insuranceBuilder.verificationRetryAttempted) {
              // Backend marked this as not verifiable, or we already retried once.
              // In both cases, skip another verification attempt and use upload fallback.
              useSessionStore
                .getState()
                .updateState({ insuranceBuilder: { ...updatedBuilder, ranVerification: true } });

              return navigate({
                to: '/insurance/upload',
                search: {
                  planType: firstUnverified.carrier.carrierType,
                  override,
                  previousPlanFamily:
                    previousPlanFamily ?? getPreviousResolvedFamily(insuranceBuilder, failedFamily),
                  ...(preserveLaterFamilies && { preserveLaterFamilies }),
                },
                replace: true,
              });
            } else {
              // First failure - flag the retry and go back to resolve with error
              useSessionStore.getState().updateState({
                insuranceBuilder: {
                  ...updatedBuilder,
                  ranVerification: true,
                  verificationRetryAttempted: true,
                },
              });

              return navigate({
                to: '/insurance/resolve',
                search: {
                  planFamily: failedFamily,
                  override,
                  previousPlanFamily:
                    previousPlanFamily ?? getPreviousResolvedFamily(insuranceBuilder, failedFamily),
                  previousUploadPlanType,
                  ...(preserveLaterFamilies && { preserveLaterFamilies }),
                },
                state: { showVerificationError: true },
                replace: true,
              });
            }
          }

          useSessionStore
            .getState()
            .updateState({ insuranceBuilder: { ...updatedBuilder, ranVerification: true } });

          const destination = getPostFamilyResolutionDestination(updatedBuilder, submittedFamily);

          if (verified.length > 0) {
            setConfirmationDestination(destination);
            return;
          }

          if (destination.type === 'resolve') {
            return navigate({
              to: '/insurance/resolve',
              search: {
                planFamily: destination.planFamily,
                previousPlanFamily: destination.previousPlanFamily,
                ...(preserveLaterFamilies && { preserveLaterFamilies }),
              },
              replace: true,
            });
          }

          if (destination.type === 'ineligible') {
            return navigate({
              to: '/insurance/ineligible',
              search: { reason: destination.reason },
              replace: true,
            });
          }

          return navigate({ to: '/insurance/benefits', replace: true });
        },
        onError: (error) => {
          logger.error('Failed to verify insurance', error);
          navigate({
            to: '/insurance/resolve',
            search: {
              planFamily: submittedFamily,
              override,
              previousPlanFamily: previousSubmittedFamily,
              previousUploadPlanType,
              ...(preserveLaterFamilies && { preserveLaterFamilies }),
            },
            replace: true,
          });
        },
      }
    );
  };

  useEffect(function verifyAndRedirectOnMount() {
    verifyAndRedirect();
    // oxlint-disable-next-line exhaustive-deps
  }, []);

  useEffect(
    function navigateAfterCoverageConfirmed() {
      if (!confirmationDestination) return;

      const timeoutId = window.setTimeout(() => {
        navigateAfterConfirmation(confirmationDestination);
      }, CONFIRMED_DELAY_MS);

      return () => window.clearTimeout(timeoutId);
    },
    // oxlint-disable-next-line exhaustive-deps
    [confirmationDestination]
  );

  const toResolve = () => {
    const activeFamily = submittedFamilyRef.current ?? getActiveVerificationInsuranceFamily();

    navigate({
      to: '/insurance/resolve',
      search: {
        planFamily: activeFamily,
        override: getResolveBackOverride({
          builder: useSessionStore.getState().insuranceBuilder,
          family: activeFamily,
          override,
        }),
        previousPlanFamily,
        previousUploadPlanType,
        ...(preserveLaterFamilies && { preserveLaterFamilies }),
      },
      replace: true,
    });
  };

  return (
    <section>
      <NavigationBar onBack={toResolve} backLabel="Back" stepIndicator="2 of 3" />
      <StepperCard currentStep={2} totalSteps={3}>
        {confirmationDestination ? (
          <CardContent
            className="flex flex-col items-center justify-center space-y-8"
            aria-label="Loading..."
            aria-busy="false"
            aria-live="polite"
            role="status"
          >
            <Heading1 className="pb-0 text-center">Verified!</Heading1>

            <div className="animate-in duration-300 zoom-in">
              <AnimatedShieldCheck className="size-[80px]" />
            </div>

            <div className="flex flex-col items-center gap-4">
              <p className="text-charcoal-700 text-center">Your coverage has been confirmed.</p>
            </div>
          </CardContent>
        ) : (
          <>
            <CardHeader>
              <CardTitle className="text-center">Verifying your plan information...</CardTitle>
            </CardHeader>
            <CardContent
              className="flex flex-col items-center justify-center space-y-8"
              aria-label="Loading..."
              aria-live="polite"
              aria-describedby="insurance-check-loader"
              aria-busy="true"
              role="status"
            >
              <Lottie animationData={animationData} className="aspect-square size-[80px]" />

              <p className="text-center text-base whitespace-pre-line">
                {`Confirming your enrollment with ${insuranceDisplayName}.\n\nThis may take a minute.`}
              </p>
            </CardContent>
          </>
        )}
      </StepperCard>
    </section>
  );
}
